import React, { FC, useMemo, useState } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import {
  Brief,
  BriefAttachment,
  BriefPdpUrl,
  briefExpertTypeOptions,
  briefPdpLinks,
  briefAssetFileName,
  briefDisclaimerRows,
  briefHasRmnAssets,
} from './constants';
import { useProfessions } from '../../hooks/useProfessions';
import { Button } from '../Button/Button';
import { downloadBriefPDF } from '../../utils/briefPdfGenerator';
import { BriefAttachmentPreview } from './BriefAttachmentPreview';
import { AttachmentRow } from './AttachmentRow';
import { downloadAttachment } from './downloadAttachment';
import { isPreviewableImage, kindLabelOf } from './attachmentKinds';

interface Props {
  brief: Brief;
  projectTitle?: string;
  showDownloadButton?: boolean;
}

export const BriefView: FC<Props> = ({
  brief,
  projectTitle,
  showDownloadButton = false,
}) => {
  const { professionsList, subProfessionLibrary } = useProfessions();
  const [isDownloading, setIsDownloading] = useState(false);
  const attachments = (brief.attachments || []).filter((a) => !!a.location);
  const [preview, setPreview] = useState<BriefAttachment | null>(null);

  const getEntry = (heading: string, text?: string) => (
    <Box className="flex flex-col gap-2">
      <Typography variant="body1" className="text-text-1">
        {heading}
      </Typography>
      <Typography
        variant="body2"
        className="text-text-2"
        sx={{ whiteSpace: 'pre-line' }}
      >
        {text || '-'}
      </Typography>
    </Box>
  );

  const disclaimers = briefDisclaimerRows(brief);

  const getFileEntry = (heading: string, url?: string | null) => (
    <Box className="flex flex-col gap-2">
      <Typography variant="body1" className="text-text-1">
        {heading}
      </Typography>
      {url ? (
        <AttachmentRow
          name={briefAssetFileName(url)}
          thumbnail={isPreviewableImage(url) ? url : undefined}
          subtitle={kindLabelOf(briefAssetFileName(url))}
          onPreview={() => window.open(url, '_blank', 'noopener,noreferrer')}
          onDownload={() => downloadAttachment(url, briefAssetFileName(url))}
        />
      ) : (
        <Typography variant="body2" className="text-text-2">
          -
        </Typography>
      )}
    </Box>
  );

  // One brand's set: '' = the supplier's brief_*, 'rmn_' = the network's.
  const getAssetEntries = (prefix: string) => {
    const value = (key: string): string | undefined =>
      (brief as any)[`${prefix}${key}`] || undefined;
    return (
      <>
        {getEntry('Brand tagline', value('brief_tagline'))}
        {getFileEntry('Logo for videos', value('brief_logo'))}
        {getEntry('Brand colors', value('brief_colors'))}
        {getFileEntry('Brand colors file', value('brief_colors_file'))}
        {getEntry('Brand font', value('brief_font'))}
        {getFileEntry('Brand font file', value('brief_font_file'))}
      </>
    );
  };

  // Chip renders a <div>, so the row that holds it has to be a <span>-based
  // Typography — a <div> inside a <p> is invalid markup.
  const getLinkRow = (url: string, pdpUrl?: BriefPdpUrl | null) => (
    <Typography
      variant="body2"
      component="span"
      className="flex flex-wrap items-center gap-2"
    >
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: 'var(--accent-text)',
          textDecoration: 'underline',
          wordBreak: 'break-all',
        }}
      >
        {url}
      </a>
      {pdpUrl?.retailer && (
        <Chip
          label={pdpUrl.retailer}
          size="small"
          className="!bg-primaryLight !text-[#7A5C24] !font-bold !text-[11px]"
        />
      )}
      {pdpUrl && !pdpUrl.active && (
        <Chip
          label="Not scored yet"
          size="small"
          className="!bg-[#F4F4F4] !text-[#8A8A8A] !text-[11px]"
        />
      )}
    </Typography>
  );

  // A brief carries one PDP link per product on its project. Older briefs only
  // ever had the single `url`, so fall back to it when no per-product rows came
  // back.
  const getPdpEntry = (heading: string) => {
    const filled = briefPdpLinks(brief);
    const showTitles = filled.length > 1;

    return (
      <Box className="flex flex-col gap-2">
        <Typography variant="body1" className="text-text-1">
          {heading}
        </Typography>
        {filled.length === 0 ? (
          <Typography variant="body2" className="text-text-2">
            -
          </Typography>
        ) : (
          filled.map((link) => (
            <Box
              key={link.desireid || link.url}
              className="flex flex-col gap-1"
            >
              {showTitles && link.product_title && (
                <Typography variant="caption" className="text-text-2">
                  {link.product_title}
                </Typography>
              )}
              {getLinkRow(link.url, link.pdpUrl)}
            </Box>
          ))
        )}
      </Box>
    );
  };

  const professionText = useMemo(() => {
    if (!brief?.professions || brief.professions.length === 0) {
      if (!brief?.profession_id) {
        return null;
      }

      const profession = professionsList.find(
        (item) => item.id === brief.profession_id
      );

      if (!brief?.sub_profession_id) {
        return profession?.name || '-';
      }

      const subProfession = subProfessionLibrary[brief.profession_id]?.find(
        (item: any) => item.id === brief.sub_profession_id
      );

      return subProfession?.name || profession?.name || '-';
    }

    const sortedProfessions = brief.professions.sort(
      (a, b) => (a.order || 0) - (b.order || 0)
    );

    const professionNames = sortedProfessions.map((prof) => {
      const profession = professionsList.find(
        (p) => p.id === prof.profession_id
      );
      const professionName = profession?.name || 'Unknown Profession';

      if (!prof.sub_profession_id) {
        return professionName;
      }

      const subProfession = subProfessionLibrary[prof.profession_id]?.find(
        (sp: any) => sp.id === prof.sub_profession_id
      );

      return subProfession?.name || professionName;
    });

    return professionNames.join(', ');
  }, [brief, professionsList, subProfessionLibrary]);

  const handleDownloadPDF = async () => {
    if (isDownloading) return;

    setIsDownloading(true);
    try {
      await downloadBriefPDF(brief, projectTitle, professionText || undefined);
      toast.success('Downloaded Brief successfully!');
    } catch {
      toast.error("Couldn't download the brief. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box className="flex flex-col gap-4">
      <Typography
        variant="h6"
        sx={{
          fontFamily: 'CircularStd',
          fontSize: '18px',
          fontWeight: 800,
          lineHeight: '120%',
          color: 'var(--text)',
        }}
      >
        Project Brief
      </Typography>
      <Box className="flex flex-col w-full">
        <Box className="flex flex-col gap-6 p-6 border border-border-token rounded-2xl bg-surface">
          <Box className="flex flex-col gap-6">
            <Box className="flex items-center justify-between">
              <Typography variant="h6" className="!text-lg">
                Expert Questions
              </Typography>
              {showDownloadButton && (
                <Button
                  onClick={handleDownloadPDF}
                  loading={isDownloading}
                  className="flex items-center gap-2"
                  variant="contained"
                >
                  {isDownloading ? 'Downloading...' : 'Download Brief'}
                </Button>
              )}
            </Box>
            {getEntry('Gender', brief.expert_gender)}
            {getEntry(
              'Age Range',
              `${brief.expert_age_min || 0} - ${brief.expert_age_max || 0}`
            )}
            {getEntry(
              'Expert Type',
              briefExpertTypeOptions.find(
                (o) => o.value === (brief as any).expert_type
              )?.label
            )}
            {getEntry('Professions/Expertise', professionText)}
            {getEntry('Interests', brief.expert_interests)}
            {getEntry('Other Requirements', brief.other)}
            {getEntry(
              'List any filming appearance requests for selected Expert',
              brief.special_requests
            )}
          </Box>

          <Box className="flex flex-col gap-6">
            <Typography variant="h6" className="!text-lg">
              Intake Form
            </Typography>
            {getPdpEntry('If the product is live, please link its PDP')}
            {getEntry(
              'List the product name as you would like it to be first introduced in the video, and include its phonetic pronunciation',
              brief.name
            )}
            {getEntry(
              'List 2-3 shortened product names that are approved to be used throughout the rest of the video',
              brief.short_name
            )}
            {getEntry(
              "List the 3-5 most important product features and benefits you'd like to be highlighted in the video in order of priority/importance. Simple language is encouraged",
              brief.description
            )}
            {getEntry(
              'List any requests for the filming location/setting',
              brief.location_request
            )}
            {getEntry(
              'Any other specific brand requests:',
              brief.brand_requests
            )}
          </Box>

          <Box className="flex flex-col gap-6">
            <Typography variant="h6" className="!text-lg">
              Brand &amp; Product Assets
            </Typography>
            {getAssetEntries('')}
            {disclaimers.length === 0
              ? getEntry('Disclaimers')
              : disclaimers.map((row) => (
                  <React.Fragment key={row.desireid}>
                    {getEntry(
                      row.title ? `Disclaimers — ${row.title}` : 'Disclaimers',
                      row.text
                    )}
                  </React.Fragment>
                ))}
          </Box>
          {briefHasRmnAssets(brief) && (
            <Box className="flex flex-col gap-6">
              <Typography variant="h6" className="!text-lg">
                Retailer Assets
              </Typography>
              {getAssetEntries('rmn_')}
            </Box>
          )}
          {attachments.length > 0 && (
            <Box className="flex flex-col gap-2">
              <Typography variant="body1" className="text-text-1">
                Attachments
              </Typography>
              <Box className="flex flex-col gap-2">
                {attachments.map((attachment) => (
                  <AttachmentRow
                    key={attachment.id}
                    name={attachment.name}
                    thumbnail={
                      isPreviewableImage(attachment.name)
                        ? attachment.location || undefined
                        : undefined
                    }
                    subtitle={kindLabelOf(attachment.name)}
                    onPreview={() => setPreview(attachment)}
                    onDownload={() =>
                      downloadAttachment(
                        attachment.location as string,
                        attachment.name
                      )
                    }
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      <BriefAttachmentPreview
        attachment={preview}
        onClose={() => setPreview(null)}
      />
    </Box>
  );
};
